// Is the Energy screen's picture current? — the pure half (tests/energy-screen).
//
// Two different things can make it old, and the screen says which:
//
//   THE DESK STOPPED. Its last pass is long before its own "now". Both stamps
//   come from the desk, in one answer, so this needs no clock of ours — and
//   says nothing about how long ago that answer was.
//
//   THE SCREEN STOPPED READING. The device went offline, or the tab slept: the
//   polling pauses WITHOUT an error, and the last answer sits there looking
//   current. Every stamp inside it is frozen at the moment it was made, so no
//   comparison between them can ever show it ageing. (A review held a snapshot
//   for an hour: "last pass 0s ago", nothing wrong.) Only the viewer's own
//   clock, against the moment the answer ARRIVED, can.
//
// The second is measured on the viewer's clock at both ends (when the answer
// arrived, and now), so a viewer whose clock is wrong is still measured right.

/** The desk's passes are 20s apart and the screen asks every 20s. Four misses of either is not a blip. */
export const STALE_AFTER_S = 90;
/**
 * How long without a pass before the desk is said to have stopped: the same
 * ninety seconds — or three of its own rounds, when it has been set to make
 * them further apart (up to five minutes). Judged by a fixed ninety, a desk
 * on a five-minute round was "stopped" for most of every round.
 */
export const stoppedAfter = (tickSec: number | undefined): number =>
  Math.max(STALE_AFTER_S, 3 * (typeof tickSec === 'number' && Number.isFinite(tickSec) && tickSec > 0 ? tickSec : 20));
/** How far behind the answer's arrival the viewer's clock may read before it is taken to have been set back. */
const CLOCK_SLACK_S = 60;

export interface EnergyFreshness {
  /** Seconds between the desk's last completed pass and its own "now"; null = it has completed none since it started. */
  sincePass: number | null;
  deskStopped: boolean;
  /**
   * Seconds this screen has held the answer it is showing, on the viewer's
   * own clock. Never negative. null = it cannot be worked out (no arrival time,
   * or a clock that was set back) — and a picture whose age is unknown is not
   * a current one.
   */
  heldFor: number | null;
  held: boolean;
}

export function energyFreshness(p: { asOf: string; tickAt: string | null; tickSec?: number; nowMs: number; receivedAtMs: number }): EnergyFreshness {
  const sincePass = p.tickAt === null ? null : (Date.parse(p.asOf) - Date.parse(p.tickAt)) / 1000;
  const raw = (p.nowMs - p.receivedAtMs) / 1000;
  // A little below zero is ordinary: the answer arrived just after the screen
  // last looked at its clock. A lot below zero is a clock that has been set
  // back — and then how long the picture has been held cannot be told.
  const heldFor = Number.isFinite(raw) && p.receivedAtMs > 0 && raw > -CLOCK_SLACK_S ? Math.max(0, Math.round(raw)) : null;
  return {
    sincePass,
    // Not a number is not "recent": stamps that cannot be read are a desk that cannot be vouched for.
    deskStopped: sincePass === null || !(sincePass <= stoppedAfter(p.tickSec)),
    heldFor,
    held: heldFor === null || heldFor > STALE_AFTER_S,
  };
}
